import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { OrganizationService } from './organization.service';
import { SupplierMasterLink, SuppliersService } from './suppliers.service';

interface LogoIndex {
  byId: Map<string, string>;
  byName: Map<string, string>;
  byDomain: Map<string, string>;
}

/** Mail domains anyone can have: a sender on one says nothing about which business they are. */
const PUBLIC_MAIL_DOMAINS = new Set(['gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
  'yahoo.com', 'yahoo.com.au', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'bigpond.com', 'bigpond.net.au',
  'optusnet.com.au', 'protonmail.com', 'proton.me', 'gmx.com', 'zoho.com']);

/** How long one organisation's logo links are reused before they are read again. */
const CACHE_MS = 10 * 60 * 1000;

/**
 * SD-1240. The master contact's logo for a supplier, for every supplier badge in the app.
 *
 * Reads the organisation's links once (`master-contacts/links`) and shares them, so a table
 * of a hundred badges makes one request. A supplier is found by its id where the row has
 * one, otherwise by its name. Nothing found -- or the request failing -- is `null`, and the
 * badge keeps its initials in the supplier's own colour.
 */
@Injectable({
  providedIn: 'root'
})
export class SupplierLogoService {
  private cache = new Map<string, { at: number; index$: Observable<LogoIndex> }>();
  private readonly changedSubject = new Subject<string>();

  /** An organisation whose logos just changed (a website saved, contacts merged): look again. */
  readonly changed$ = this.changedSubject.asObservable();

  constructor(private suppliersService: SuppliersService, private organizationService: OrganizationService) { }

  /**
   * The logo by supplier id, else name, else -- given a sender's email address -- the contact whose
   * website is on the sender's domain (or a parent of it: billing.acme.com finds acme.com).
   */
  logoFor(supplierId?: string, supplierName?: string, organizationId?: string, email?: string): Observable<string | null> {
    const id = key(supplierId);
    const name = key(supplierName);
    const domain = mailDomain(email);
    if (!id && !name && !domain) return of(null);

    return this.index(organizationId || this.organizationService.selectedOrganization).pipe(
      map(ix => (id && ix.byId.get(id)) || (name && ix.byName.get(name)) || (domain && byDomain(ix, domain)) || null)
    );
  }

  /** Drops the organisation's links, so the next badge reads them again, and says so. */
  invalidate(organizationId?: string): void {
    const org = organizationId || this.organizationService.selectedOrganization;
    if (!org) { return; }
    this.cache.delete(org);
    this.changedSubject.next(org);
  }

  private index(organizationId: string): Observable<LogoIndex> {
    if (!organizationId) return of(emptyIndex());

    const hit = this.cache.get(organizationId);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.index$;

    const index$ = this.suppliersService.getMasterContactLinks(organizationId).pipe(
      map(links => buildIndex(links)),
      catchError(() => of(emptyIndex())),
      shareReplay(1)
    );
    this.cache.set(organizationId, { at: Date.now(), index$ });
    return index$;
  }
}

function buildIndex(links: SupplierMasterLink[]): LogoIndex {
  const ix = emptyIndex();
  (links || []).forEach(l => {
    if (!l.logoUrl) return;
    const id = key(l.supplierId);
    const name = key(l.supplierName);
    if (id) ix.byId.set(id, l.logoUrl);
    // Two suppliers with one name and different logos: neither is certain, so show initials.
    if (name) ix.byName.set(name, ix.byName.has(name) && ix.byName.get(name) !== l.logoUrl ? null : l.logoUrl);
    // The same rule for a domain two contacts with different logos share.
    (l.domains || []).map(d => key(d).replace(/^www\./, '')).filter(d => !!d && !PUBLIC_MAIL_DOMAINS.has(d)).forEach(d =>
      ix.byDomain.set(d, ix.byDomain.has(d) && ix.byDomain.get(d) !== l.logoUrl ? null : l.logoUrl));
  });
  return ix;
}

function emptyIndex(): LogoIndex {
  return { byId: new Map(), byName: new Map(), byDomain: new Map() };
}

/** The part after the @, unless it is a public mail domain. */
function mailDomain(email?: string): string {
  const at = (email || '').lastIndexOf('@');
  const domain = at < 0 ? '' : key(email.slice(at + 1)).replace(/[>\s]+$/, '');
  return domain && !PUBLIC_MAIL_DOMAINS.has(domain) ? domain : '';
}

/** The domain itself, else each parent down to two labels (billing.acme.com.au -> acme.com.au). */
function byDomain(ix: LogoIndex, domain: string): string | null {
  const parts = domain.split('.');
  for (let i = 0; i <= parts.length - 2; i++) {
    const hit = ix.byDomain.get(parts.slice(i).join('.'));
    if (hit) { return hit; }
  }
  return null;
}

function key(value: string): string {
  return (value || '').toString().trim().toLowerCase();
}
